import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useJsApiLoader } from "@react-google-maps/api";
import { Resizable } from "re-resizable";

import useLanguageChange from "../../hooks/useLanguageChange";
import {
    setIsLatLongDisable,
    setIsLiveLocationMapOpen,
    setRemoteParticipantsLocationInfo,
    setSelectedParticipant,
} from "../../reducers/live-location-reducer";
import { getParticipantIndex } from "../../shared/dataTrackFunction";
import GoogleMapBox from "./GoogleMapBox";
import { DEVICE_ORIENTATION_TYPES, GOOGLE_MAP_API_KEY } from "../../constants/Constants";
import navigatorService from "../../services/navigatorService";
import { PublicVariables } from "../../constants/PublicVariables";
import { takeLiveMapImage } from "./LocationUtils";

const libraries = ["geometry"];

const LiveLocation = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const { isLiveLocationMapOpen, remoteParticipantsLocationInfo, selectedParticipant, updateLatLong, isSaveBtnDisabled } = useSelector(
        (state) => state.liveLocation
    );
    const { callParticipants, isBookmarkOpen, isChatDrawerOpen, isNotesDrawerOpen, isParticipantDrawerOpen } = useSelector(
        (state) => state.commonReducer
    );

    const [zoomControl, setZoomControl] = useState(true);
    const mapRef = useRef(null);
    const polyRef = useRef(null);

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: GOOGLE_MAP_API_KEY,
        libraries,
    });

    const handleOrientationChange = () => {
        const currentOrientation = navigatorService.getOrientation();
        const isDeviceMobileIpad = navigatorService.isIpadOS() || navigatorService.mobileIpad();

        if (PublicVariables.isMobile) {
            if (
                currentOrientation === DEVICE_ORIENTATION_TYPES.THREE_LANDSCAPE ||
                currentOrientation === DEVICE_ORIENTATION_TYPES.FOUR_LANDSCAPE_REVERSED
            ) {
                setZoomControl(isDeviceMobileIpad);
            } else {
                setZoomControl(true);
            }

            if (isBookmarkOpen || isChatDrawerOpen || isNotesDrawerOpen || isParticipantDrawerOpen) {
                if (
                    (currentOrientation === DEVICE_ORIENTATION_TYPES.ONE_PORTRAIT ||
                        currentOrientation === DEVICE_ORIENTATION_TYPES.TWO_UPSIDE_DOWN) &&
                    !isDeviceMobileIpad
                ) {
                    dispatch(setIsLiveLocationMapOpen(false));
                }
            }
        } else {
            setZoomControl(true);
        }
    };

    useEffect(() => {
        if (!isLiveLocationMapOpen && remoteParticipantsLocationInfo.length > 0) {
            dispatch(setSelectedParticipant(remoteParticipantsLocationInfo[0]));
        }
    }, [isLiveLocationMapOpen]);

    useEffect(() => {
        window.addEventListener("orientationchange", handleOrientationChange);
        return () => window.removeEventListener("orientationchange", handleOrientationChange);
    }, [isBookmarkOpen, isChatDrawerOpen, isNotesDrawerOpen, isParticipantDrawerOpen]);

    useEffect(() => {
        const remoteParticipants = [];
        callParticipants.forEach((user) => {
            if ("isAgent" in user && !user.isAgent && user.latitude != 0 && user.longitude != 0 && user.latitude != "" && user.longitude != "") {
                const userInfo = { identity: user.identity, lat: parseFloat(user.latitude), long: parseFloat(user.longitude) };
                const index = getParticipantIndex(user.identity);
                if (index) userInfo.label = index;
                remoteParticipants.push(userInfo);
            }
        });
        dispatch(setRemoteParticipantsLocationInfo(remoteParticipants));

        dispatch(setIsLatLongDisable(isEmpty(remoteParticipants)));

        setTimeout(() => {
            setUpdatedSelectedParticipant(remoteParticipants);
        }, 100);
    }, [callParticipants]);

    const setUpdatedSelectedParticipant = (remoteParticipants) => {
        if (isEmpty(remoteParticipants)) return; // If no remote participants, do nothing

        if (isEmpty(selectedParticipant)) {
            // If no participant is currently selected, select the first one
            dispatch(setSelectedParticipant(remoteParticipants[0]));
        } else {
            // Check if the selected participant is still in the remote participants list
            const currentParticipant = remoteParticipants.find((user) => user.identity === selectedParticipant.identity);
            if (!currentParticipant) {
                // If selected participant left the call, select the first one
                dispatch(setSelectedParticipant(remoteParticipants[0]));
            } else {
                // If the participant is still in the call, update its properties
                dispatch(
                    setSelectedParticipant({
                        ...currentParticipant,
                        lat: selectedParticipant.lat,
                        long: selectedParticipant.long,
                        label: selectedParticipant.label,
                    })
                );
            }
        }
    };

    const drawRouteData = useMemo(() => {
        const routeData = [];

        updateLatLong.forEach((data, i) => {
            if (data.identity === selectedParticipant.identity) {
                routeData.push({
                    lat: data.lat,
                    lng: data.long,
                });
            }
        });
        return routeData;
    }, [selectedParticipant, updateLatLong]);

    const startingLatLong = useMemo(() => {
        return drawRouteData[0] || {};
    }, [drawRouteData]);

    const handleCloseLiveLocationMap = () => {
        dispatch(setIsLiveLocationMapOpen(false));
    };

    const handleOnParticipantChange = (userInfo) => {
        dispatch(setSelectedParticipant(userInfo));
    };

    const handleClickOnSave = () => {
        takeLiveMapImage(polyRef.current?.state?.polyline, drawRouteData);
    };

    return (
        <div className={clsx("live-location-modal", isLiveLocationMapOpen && "active")} id='live-location-modal' data-testid='live-location-modal'>
            <div className='location-modal-close'>
                <button className='btn close-btn' onClick={handleCloseLiveLocationMap}>
                    <span className='btn-icon icon-close-light'></span>
                </button>
            </div>
            <div className='location-save-image-button custom-tooltip tooltip-auto-width m-hide'>
                <button
                    className={clsx("btn btn-primary save-btn", isSaveBtnDisabled && "no-drop-cursor")}
                    onClick={handleClickOnSave}
                    disabled={isSaveBtnDisabled}
                >
                    <span className='btn-icon icon-save-image'></span>
                </button>
                <div className='custom-tooltip-content text-nowrap'>
                    <div className='custom-tooltip-in'>
                        <p>{localisedValues["save_image"]}</p>
                    </div>
                </div>
            </div>
            <div className='dropdown custom-dropdown dropdown-select location-participant-dropdown'>
                <button
                    className='btn dropdown-toggle triangle-none'
                    type='button'
                    data-bs-toggle='dropdown'
                    aria-expanded='false'
                    disabled={remoteParticipantsLocationInfo.length < 2}
                    data-testid='remoteUserDropdown'
                >
                    <span className='btn-text'>{selectedParticipant?.label}</span>
                    <span className='dropdown-toggle-arrow icon-down-arrow-light'></span>
                </button>
                <div className='dropdown-menu'>
                    <div className='dropdown-menu-in custom-scroll-thin'>
                        {remoteParticipantsLocationInfo.length > 1 && (
                            <ul>
                                {remoteParticipantsLocationInfo.map((user) => {
                                    const isSelected = user.identity === selectedParticipant.identity;
                                    return (
                                        <li key={user.identity} data-testid='remoteUserInfoDiv'>
                                            <button
                                                className={clsx("btn dropdown-item", isSelected && "active")}
                                                onClick={() => handleOnParticipantChange(user)}
                                            >
                                                {user.label}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            {isLoaded && (
                <Resizable
                    className='resizable-location-map'
                    handleStyles={{
                        left: { cursor: "e-resize" },
                        top: { cursor: "n-resize" },
                    }}
                    defaultSize={{ height: 315, width: 315 }}
                    enable={{ top: true, left: true }}
                >
                    <GoogleMapBox
                        ref={{ mapRef, polyRef }}
                        zoomControl={zoomControl}
                        startingLatLong={startingLatLong}
                        drawRouteData={drawRouteData}
                    />
                </Resizable>
            )}
        </div>
    );
};

export default LiveLocation;
